import React from 'react';
import { Select, Text, Flex, Box } from '@scale/scaleui-radix';
import {
  INSERT_ORDER_OPTIONS,
  parseInsertOrderValue,
  insertOrderValueToId,
} from './projectInviteConstants';

type InsertOrderSelectorProps = {
  /**
   * The current insert order value (undefined = push to end)
   */
  value: number | undefined;
  /**
   * Callback when the insert order changes
   */
  onChange: (value: number | undefined) => void;
  /**
   * Whether the selector is disabled
   */
  disabled?: boolean;
  /**
   * Whether to show the label and description
   */
  showLabel?: boolean;
  /**
   * Custom className for the trigger
   */
  className?: string;
};

/**
 * Selector for assignment rank / insert order
 * Controls where the assignment will be inserted into the users' assignment list
 */
export const InsertOrderSelector: React.FC<InsertOrderSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  showLabel = true,
  className = 'w-full',
}) => {
  const handleValueChange = (newValue: string) => {
    onChange(parseInsertOrderValue(newValue));
  };

  return (
    <Box>
      <Flex direction="column" gap="1">
        {showLabel && (
          <Text size="2" weight="medium">
            Assignment Rank
          </Text>
        )}
        <Select.Root
          value={insertOrderValueToId(value)}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          <Select.Trigger className={className} />
          <Select.Content>
            {INSERT_ORDER_OPTIONS.map(option => (
              <Select.Item key={option.id} value={option.id}>
                {option.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {showLabel && (
          <Text size="2" weight="light" color="neutral">
            Controls where the assignment will be inserted into the users' assignment list. "Push to
            end" adds the assignment to the end of the list, while specific positions insert at that
            position and push existing assignments down.
          </Text>
        )}
      </Flex>
    </Box>
  );
};
